import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Form,
  Divider,
  Modal,
  Input,
  Button as AntdButton,
  Typography,
  Card,
  Skeleton,
} from 'antd';
import TemplateSelectDropdown from './Settings.TemplateDropdown';
import TemplateBuilder from './Settings.TemplateBuilder';
import './Settings.scss';
import { Button } from '../baseComponents/Button';
import { AlertIcon } from 'src/icons';
import { analytics } from 'src/services';
import {
  createTemplateOverride,
  listDotPhraseOverrides,
  updateTemplateOverride,
  deleteCustomTemplateAction,
  changePrebuiltTemplate,
} from '../../store/actions/settings';
import { updateCurrentPractitionerSettings } from 'src/store/actions/practitioner.action';
import { useSelector } from 'react-redux';
import { isEmpty, orderBy } from 'lodash-es';
import {
  QuestionCircleOutlined,
  CopyOutlined,
  DeleteOutlined,
  CheckOutlined,
} from '@ant-design/icons';
import { SupaDemo } from '../../../../components/groupComponents/supaDemo';
import { useAuth } from '@frontegg/react';
import { showToastError } from '../../../../utilities/errortoast';
import { useBeforeUnload } from '@insight-health-ai/roseate/utils';
import { showToastSuccess } from 'src/utilities/toast';
import { customFormValidationRules } from 'src/utils';

const phaseToTemplateType = {
  'in-visit': {
    templateType: 'ambient_in_visit_template_id',
    templateOverridesType: 'ambient_in_visit_template_overrides_id',
  },
  'pre-chart': {
    templateType: 'ambient_pre_chart_template_id',
    templateOverridesType: 'ambient_pre_chart_template_overrides_id',
  },
  'after-visit': {
    templateType: 'ambient_after_visit_template_id',
    templateOverridesType: 'ambient_after_visit_template_overrides_id',
  },
};

const SettingsTemplates = ({
  encounterPhase = 'in-visit',
  template_overrides_id = null,
}) => {
  const dispatch = useDispatch();
  const isMobile = window.innerWidth < 1260;

  const [workingTemplate, setWorkingTemplate] = useState({});
  const [nextWorkingTemplate, setNextWorkingTemplate] = useState({});
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [form] = Form.useForm();
  const [selectedDefaultTemplate, setSelectedDefaultTemplate] = useState(false);

  const [isDeleteTemplateModalVisible, setIsDeleteTemplateModalVisible] =
    useState(false);
  const { user } = useAuth();

  const { dotPhraseOverridesList } = useSelector(
    (state) => state?.settingsState
  );

  const { templateList: defaulTemplates, templateOverridesList } = useSelector(
    (state) => state?.settingsState
  );

  const { currentPractitionerSettings } = useSelector(
    (state) => state?.practitionerState
  );
  const isDefaultTemplate = useMemo(() => {
    if (
      !workingTemplate ||
      (!workingTemplate?.template_id && !workingTemplate?.template_overrides_id)
    ) {
      return false;
    }
    return !!defaulTemplates.find(
      (template) => template.template_id === workingTemplate.template_id
    );
  }, [defaulTemplates, workingTemplate]);

  // Check if template is loading (no template data available)
  const isTemplateLoading = useMemo(() => {
    return (
      !workingTemplate ||
      (!workingTemplate?.template_id && !workingTemplate?.template_overrides_id)
    );
  }, [workingTemplate, templateOverridesList]);

  useBeforeUnload({ enabled: !!hasUnsavedChanges });

  const defaultTemplateCheck = useMemo(() => {
    const templateType = phaseToTemplateType[encounterPhase];
    if (templateType) {
      return (
        currentPractitionerSettings?.[templateType?.templateOverridesType] ===
          workingTemplate?.template_overrides_id ||
        currentPractitionerSettings?.[templateType?.templateType] ===
          workingTemplate?.template_id
      );
    }
    return false;
  }, [workingTemplate, currentPractitionerSettings, encounterPhase]);

  useEffect(() => {
    dispatch(listDotPhraseOverrides());
  }, [dispatch]);

  const openCreateCopyModal = () => {
    setIsModalVisible(true);
    form.setFieldsValue({
      name: `${workingTemplate.name || ''} (copy)`,
      description: workingTemplate.description || '',
    });
    analytics.track('Clicked Create A Copy In Template Builder', {
      email: user?.email,
    });
  };

  const onDelete = () => {
    setIsDeleteTemplateModalVisible(true);
  };
  const deleteCopy = () => {
    analytics.track('Deleted Template In Template Builder Page', {
      templateId: workingTemplate.template_overrides_id,
      email: user?.email,
    });
    if (workingTemplate.template_overrides_id == undefined) {
      return;
    }

    dispatch(
      deleteCustomTemplateAction(
        encounterPhase,
        workingTemplate.template_overrides_id,
        setIsDeleteTemplateModalVisible,
        setWorkingTemplate,
        nextWorkingTemplate
      )
    ).then(() => {
      setWorkingTemplate({});
    });
  };

  const lastTemplateChange = useRef(null);
  useEffect(() => {
    if (workingTemplate.template_overrides_id !== lastTemplateChange.current) {
      setHasUnsavedChanges(false);
      lastTemplateChange.current = workingTemplate.template_overrides_id;
    }
  }, [workingTemplate]);

  useEffect(() => {
    if (!workingTemplate) {
      let sortedTemplatesListByDate = orderBy(
        templateOverridesList,
        ['created_at'],
        ['desc']
      );

      if (!isEmpty(sortedTemplatesListByDate)) {
        // selected template should be the most recent one
        const selectedTemplate = sortedTemplatesListByDate[0];

        if (!isEmpty(selectedTemplate)) {
          setWorkingTemplate(selectedTemplate);
        }
      }
    }
  }, [templateOverridesList, workingTemplate]);

  const handleSaveAsNew = () => {
    analytics.track(
      'Clicked Save As New When Creating A Copy In Template Builder',
      { email: user?.email }
    );
    form
      .validateFields()
      .then((values) => {
        const newTemplate = { ...workingTemplate, ...values };

        return dispatch(
          createTemplateOverride(encounterPhase, {
            name: newTemplate.name,
            description: newTemplate.description,
            body: newTemplate.body,
            prompt_screen: 'ambient_summary',
            restrict_verbosities: newTemplate.restrict_verbosities || [],
          })
        );
      })
      .then((newTemplate) => {
        setWorkingTemplate(newTemplate);
        setIsModalVisible(false);
      })
      .catch(() => {});
  };

  const handleSave = (template) => {
    return dispatch(
      updateTemplateOverride(
        encounterPhase,
        template?.template_overrides_id
          ? template?.template_overrides_id
          : template?.template_id,
        {
          name: template.name,
          description: template.description,
          body: template.body,
        }
      )
    )
      .then(() => {
        setHasUnsavedChanges(false);
      })
      .catch(() => {
        showToastError('Failed to update template!');
      });
  };

  const onTemplateChange = (template, autoSave = false) => {
    setHasUnsavedChanges(true);
    setWorkingTemplate(template);
    if (autoSave) {
      handleSave(template);
    }
  };

  const handleUnsavedTemplatePrompt = (template) => {
    const { destroy } = Modal.warn({
      closable: true,
      footer: (
        <div className="flex justify-end gap-2 mt-4">
          <AntdButton
            className="ih-button-primary"
            type="button"
            onClick={() =>
              handleSave(workingTemplate).then(() => {
                destroy();
              })
            }
          >
            Save and proceed
          </AntdButton>
          <AntdButton
            className="ih-button-negative"
            type="button"
            onClick={() => {
              setWorkingTemplate(template);
              destroy();
            }}
          >
            Proceed anyway
          </AntdButton>
        </div>
      ),
      title: 'Discard unsaved changes?',
      content:
        'This will discard all the unsaved changes you made to the current template.',
    });
  };

  const onTemplateSelectionChange = (template, type) => {
    if (hasUnsavedChanges && type === 'userSelected') {
      handleUnsavedTemplatePrompt(template);
    } else {
      setWorkingTemplate(template);
    }
  };

  return (
    <>
      <div>
        <div
          className={`settings-component flex flex-col gap-4 xl:w-10/12 ${
            isMobile ? 'mb-16' : ''
          }`}
        >
          <Card className="shadow-md bg-white rounded-xl">
            <label
              htmlFor="template-select"
              className="text-black font-bold text-[18px] flex items-center"
            >
              Select note template
              <SupaDemo>
                <span
                  data-supademo-demo="clz07qg9i2ibdz9kd5sj5nz70"
                  className="ml-4 cursor-pointer ih-button-ghost gap-2 text-[#5a8dda] underline"
                  onClick={() => {
                    analytics.track('Clicked Custom Template Guided Tour ', {
                      email: user?.email,
                    });
                  }}
                >
                  <QuestionCircleOutlined /> Guided Tour
                </span>
              </SupaDemo>
            </label>
            <div className="h-full">
              <TemplateSelectDropdown
                className=""
                isControlled
                encounterPhase={encounterPhase}
                currentTemplate={workingTemplate}
                templateOverridesId={template_overrides_id}
                setCurrentTemplate={onTemplateSelectionChange}
                fromIsSettings={true}
                setNextWorkingTemplate={setNextWorkingTemplate}
                fromAuraPage={false}
                setSelectedDefaultTemplate={setSelectedDefaultTemplate}
              />
            </div>
            <div className="mt-4">
              <span
                className={`cursor-pointer gap-2 text-[#5a8dda] underline ${
                  defaultTemplateCheck ? 'opacity-50' : ''
                }`}
                onClick={() => {
                  if (defaultTemplateCheck) {
                    return;
                  }
                  const encounterPhaseMessage =
                    encounterPhase === 'in-visit'
                      ? 'In-Visit'
                      : encounterPhase === 'pre-chart'
                        ? 'Pre-Chart'
                        : 'After-Visit';
                  if (selectedDefaultTemplate) {
                    dispatch(
                      changePrebuiltTemplate(
                        encounterPhase,
                        workingTemplate.template_id,
                        true
                      )
                    );
                    analytics.track(
                      `Set Pre-Built Template "${workingTemplate.name}" As Default For ${encounterPhaseMessage}`,
                      {
                        email: user?.email,
                      }
                    );
                  } else {
                    dispatch(
                      updateCurrentPractitionerSettings({
                        [phaseToTemplateType[encounterPhase]
                          ?.templateOverridesType]:
                          workingTemplate.template_overrides_id || null,
                        [phaseToTemplateType[encounterPhase]?.templateType]:
                          workingTemplate.template_id || null,
                      })
                    ).then(() => {
                      showToastSuccess('Template updated');
                    });
                    analytics.track(
                      `Set Custom Template "${workingTemplate.name}" As Default For ${encounterPhaseMessage}`,
                      {
                        email: user?.email,
                      }
                    );
                  }
                }}
              >
                Make this as a default template
              </span>
            </div>
          </Card>
          <Card className="shadow-md bg-white rounded-xl">
            <div className="flex items-center justify-between mb-4 gap-2 flex-wrap md:flex-nowrap">
              {isTemplateLoading ? (
                <Skeleton.Input active size="default" className="w-32" />
              ) : (
                <span className="text-xl font-medium">Edit Template</span>
              )}
              <div className="flex flex-wrap gap-2">
                {isTemplateLoading ? (
                  <>
                    <Skeleton.Button active size="default" className="w-20" />
                    <Skeleton.Button active size="default" className="w-32" />
                    <Skeleton.Button active size="default" className="w-20" />
                  </>
                ) : (
                  <>
                    {!isDefaultTemplate && (
                      <>
                        <AntdButton
                          className="ih-button-negative left-0 mr-auto"
                          onClick={onDelete}
                        >
                          <DeleteOutlined />
                          Delete
                        </AntdButton>
                        <Divider className="h-[38px]" type="vertical" />
                      </>
                    )}
                    <AntdButton
                      htmlType="button"
                      className="ih-button-primary"
                      onClick={openCreateCopyModal}
                    >
                      <CopyOutlined />
                      Create a copy
                    </AntdButton>
                    {!isDefaultTemplate && (
                      <AntdButton
                        title="No unsaved changes"
                        htmlType="button"
                        className="ih-button-primary"
                        onClick={() => handleSave(workingTemplate)}
                        disabled={!hasUnsavedChanges}
                      >
                        <CheckOutlined />
                        Update
                      </AntdButton>
                    )}
                  </>
                )}
              </div>
            </div>
            {isTemplateLoading ? (
              <div className="space-y-4">
                <Skeleton.Input active size="large" className="w-full" />
                <Skeleton.Input active size="large" className="w-full" />
                <Skeleton.Input active size="large" className="w-full" />
                <Skeleton.Input active size="large" className="w-full" />
              </div>
            ) : (
              <TemplateBuilder
                template={workingTemplate}
                isDefaultTemplate={isDefaultTemplate}
                onChange={onTemplateChange}
                dotPhrases={dotPhraseOverridesList}
              />
            )}
          </Card>
          {/* <div className="w-full md:w-full">
            <div className={`mb-5  w-full`}></div>
          </div> */}
          <Modal
            title={
              <div className="flex flex-col mb-4">
                <Typography.Text>Create new template</Typography.Text>
                <Typography.Text
                  style={{ color: '#808080', fontWeight: '400' }}
                  className="text-sm"
                >
                  We will create a copy of current template with following
                  details. You can select it from the list of templates.
                </Typography.Text>
              </div>
            }
            open={isModalVisible}
            centered={true}
            onCancel={() => setIsModalVisible(false)}
            footer={null}
            className="settings-modal w-620px max-w-full"
          >
            <Form
              className="font-sans"
              form={form}
              layout="vertical"
              initialValues={{
                name: '',
                description: '',
              }}
            >
              <Form.Item
                name="name"
                label={
                  <p className="font-sans text-md font-medium text-[#000000]">
                    Name:
                  </p>
                }
                rules={[
                  {
                    required: true,
                    message: 'Please input the template name',
                  },
                  customFormValidationRules.noLeadingOrTrailingSpaces({
                    message:
                      'Template name cannot have leading or trailing spaces',
                  }),
                ]}
              >
                <Input className="font-sans" />
              </Form.Item>
              <Form.Item
                name="description"
                label={
                  <p className="font-sans text-md font-medium text-[#000000]">
                    Description:
                  </p>
                }
              >
                <Input.TextArea rows={16} className="font-sans" />
              </Form.Item>
              <Form.Item>
                <div className="flex flex-row justify-between space-x-2">
                  <Button
                    className={`!bg-[#808080] hover:!bg-[#A9A9A9] !border-[#808080] hover:!border-[A9A9A9]
                                            ${
                                              !workingTemplate?.template_overrides_id
                                                ? 'ml-auto'
                                                : ''
                                            }
                                        `}
                    type="cancel"
                    key="back"
                    onClick={() => {
                      setIsModalVisible(false);
                      analytics.track(
                        'Clicked Cancel When Creating A Copy In Template Builder',
                        { email: user?.email }
                      );
                    }}
                  >
                    Cancel
                  </Button>
                  <div className="flex gap-3">
                    <Button
                      type="button"
                      key="saveAsNew"
                      onClick={handleSaveAsNew}
                    >
                      Save as New
                    </Button>
                  </div>
                </div>
              </Form.Item>
            </Form>
          </Modal>
          <Modal
            open={isDeleteTemplateModalVisible}
            centered={true}
            closable={false}
            onCancel={() => setIsDeleteTemplateModalVisible(false)}
            footer={null}
          >
            <div className="flex justify-center font-sans font-semibold text mb-3 gap-2">
              <span>
                <AlertIcon />
              </span>
              <span className="text-black font-sans font-medium text-lg">
                Are you sure you want to delete the{' '}
                <b style={{ marginInline: '.5ch' }}> {workingTemplate.name} </b>{' '}
                template ?{' '}
              </span>
            </div>
            <div className="flex justify-end flex-row gap-2 ">
              <Button
                type="button"
                className="bg-[#808080] hover:bg-[#A9A9A9] border-[#808080] hover:border-[A9A9A9]"
                onClick={() => setIsDeleteTemplateModalVisible(false)}
              >
                Cancel
              </Button>
              <Button
                type="button"
                className=" bg-[#d63232] hover:bg-[#a30000] border-[#d63232] hover:border-[#a30000] "
                onClick={deleteCopy}
              >
                Delete
              </Button>
            </div>
          </Modal>
        </div>
      </div>
    </>
  );
};

export default SettingsTemplates;
